import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Mail, Lock, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";

export const SecureAccountCard = () => {
  const { user, secureAccount } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  if (!user?.anonymous) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await secureAccount(email, password, name || undefined);
      toast.success("Account saved! You can now log in on any device — your progress is safe.");
      setOpen(false);
    } catch (err) { toast.error(apiErr(err)); }
    setLoading(false);
  };

  return (
    <div data-testid="secure-account-card" className="flex flex-col gap-4 rounded-2xl border border-[#4A7c59]/30 bg-[#4A7c59]/[0.07] p-5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-[#4A7c59]"><ShieldCheck strokeWidth={1.8} className="h-5 w-5" /></div>
        <div>
          <p className="font-head font-semibold text-[#2C363F]">You're browsing anonymously as <span className="text-[#4A7c59]">{user.display_name}</span></p>
          <p className="mt-1 text-sm text-muted-foreground"><strong className="text-[#8a4436]">Your progress isn't saved permanently.</strong> Your taper data, daily logs and journal history live only in this session and <strong>will be lost</strong> if you close the tab or continue anonymously again. Add an email &amp; password to keep everything safe and log back in on any device. Your data stays private.</p>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button data-testid="secure-account-btn" className="shrink-0 rounded-full bg-[#4A7c59] hover:bg-[#3B6347]">Save my account</Button>
        </DialogTrigger>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-head">Save your account</DialogTitle>
            <DialogDescription>Add login details so you never lose your progress. Everything you've tracked stays exactly as it is.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-sm">Display name (optional)</Label>
              <div className="relative mt-1"><User className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                <Input data-testid="secure-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="A name or nickname" className="rounded-xl py-6 pl-10" /></div>
            </div>
            <div>
              <Label className="text-sm">Email</Label>
              <div className="relative mt-1"><Mail className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                <Input data-testid="secure-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="rounded-xl py-6 pl-10" /></div>
            </div>
            <div>
              <Label className="text-sm">Password</Label>
              <div className="relative mt-1"><Lock className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                <Input data-testid="secure-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="rounded-xl py-6 pl-10" /></div>
            </div>
            <Button data-testid="secure-submit" type="submit" disabled={loading} className="w-full rounded-full bg-[#4A7c59] py-6 text-base font-semibold hover:bg-[#3B6347]">
              {loading ? "Saving…" : "Save my account"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
